import axios from "axios";
import config from "./config";
import { IApp } from "./store/appStoreTypes";
import { IApiHeader } from "./store/appStoreTypes";
import {
  getStorage,
  setAddLikeProjects,
  setDeleteLikeProjects,
  setStorage,
} from "./utils/storage";
// import { fbApiEvent } from "./utils/firebaseEvent";

const APIGateways = axios.create({
  baseURL: config.apiUrl,
  headers: {
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Headers": "Origin, Content-Type, Accept",
    "Content-type": "application/json",
  },
});

/**
 * recode firebase event and call api
 * @param url call api url
 * @param method api method
 * @param params fb log params
 * @param eventName fb log event name
 * @param token app token
 * @returns {data, error} api response data, error
 */
const requestAPIAndFireBaseEvent = async ({
  url,
  method,
  params,
  eventName: name,
  token,
}: IRequestAPI) => {
  // const firebaseEventArgs = { name, params, method };
  try {
    // fbApiEvent({ ...firebaseEventArgs, type: "start" });
    if (!token && name === "getToken") {
      const result = await APIGateways.post(`/token`, JSON.parse(params));
      if (result.status === 200) {
        return result.data;
      }
    } else {
      const { data } =
        method !== "post"
          ? await APIGateways[method](url, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : await APIGateways[method](
              url,
              {},
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

      if (method === "delete" && name === "deleteLikeCount") {
        return setDeleteLikeProjects(JSON.parse(params).id);
      }

      if (!data?.success) throw new Error(`${name} fail`);
      if (name === "addLikeCount") {
        setAddLikeProjects(JSON.parse(params).id);
      }
      return data;
    }
  } catch (err) {
    if (name === "getProjectDetail") {
      // 없는 detail Id의 경우 code 404 포함
      if (err.message.includes("404")) return { error: true, code: 404 };
    }
    // fbApiEvent({ ...firebaseEventArgs, type: "error" });
    console.error(`${name} Error: ${err}`);
    return { error: true };
  } finally {
    // fbApiEvent({ ...firebaseEventArgs, type: "end" });
  }
};

export const getToken = async (req: IApp) => {
  const { appSecret } = config;

  return await requestAPIAndFireBaseEvent({
    url: `/token`,
    method: "post",
    params: JSON.stringify({ ...req, appSecret }),
    eventName: "getToken",
  });
};
interface IRequestAPI {
  url: string;
  method: "get" | "post" | "delete";
  params: string;
  eventName: string;
  token?: string;
}

// requestAPIAndFireBaseEvent를 적용한 api
export const getCategories = ({ language, token }: IApiHeader) => {
  if (!token) return { data: [], error: true };
  return requestAPIAndFireBaseEvent({
    url: `/v1/categories?language=${language}`,
    method: "get",
    params: JSON.stringify({ language, token }),
    eventName: "getCategories",
    token,
  });
};

export const getCategoriesNewCheck = async ({ token }: IApiHeader) => {
  if (!token) return { data: [], error: true };

  const todayDate = new Date().toISOString();
  const publishedAt = getStorage("prev_date")
    ? getStorage("prev_date")
    : todayDate;
  setStorage("prev_date", todayDate);

  return requestAPIAndFireBaseEvent({
    url: `/v1/check-new-project?publishedAt=${publishedAt}`,

    method: "get",
    params: JSON.stringify({ token }),
    eventName: "getCategoriesNewCheck",
    token,
  });
};

interface IProjectProps extends IApiHeader {
  /* 
  projects: 홈 feed 화면 projects, 
  v1_search: detail hashtag 검색, 
  v2_search: search 페이지 내 검색 
  */
  type: "projects" | "v1_search" | "v2_search";
  page: number;
  display: number;
  tag?: string;
  categoryId?: string;
}
export const getProjects = async ({
  token,
  language,
  type,
  page,
  display,
  categoryId = "",
  tag = "",
}: IProjectProps) => {
  if (!token) return { data: [], error: true };
  const params = JSON.stringify({
    token,
    language,
    type,
    page,
    display,
    categoryId: "",
    tag: "",
  });

  let encodeTag = tag.includes("percent2e5")
    ? tag.replace(/percent2e5/gi, "%25")
    : tag;
  encodeTag = decodeURIComponent(encodeTag).substring(0, 30);
  encodeTag = encodeURIComponent(encodeTag);

  let requestUrl;
  if (type === "projects") {
    // home feed 리스트
    requestUrl = `/v1/projects?language=${language}&page=${page}&display=${display}&category=${categoryId}`;
  } else if (type === "v1_search") {
    // detail page hashtag 클릭 검색시
    // requestUrl = `/v1/search?language=${language}&page=${page}&display=${display}&tag=${encodeTag}`;
    requestUrl = `/v2/search?language=${language}&page=${page}&display=${display}&tag=${encodeTag}&sortBy=download`;
  } else {
    // 검색관련 (최근검색어, 자동완성, 검색)
    requestUrl = `/v2/search?language=${language}&page=${page}&display=${display}&tag=${encodeTag}&sortBy=download`;
  }

  return requestAPIAndFireBaseEvent({
    url: requestUrl,
    method: "get",
    params,
    eventName: "getProjects",
    token,
  });
};
interface IRecommendHashTagProps extends IApiHeader {
  tag?: string;
  page: number;
  display: number;
  type: "hashtag" | "title";
}
// type hashtag인 경우 검색시 앞에 "#" 이 붙음
// type title인 경우 검색시 앞에 "#" 이 붙지 않음
export const getHashTags = async ({
  token,
  language,
  page,
  display,
  tag = "",
  type,
}: IRecommendHashTagProps) => {
  if (!token) return { data: [], error: true };
  const encodeTag = encodeURIComponent(tag);
  const requestUrl =
    type === "hashtag"
      ? `/v1/count?language=${language}&page=${page}&display=${display}&tag=${encodeTag}`
      : `/v2/search-auto-complete?language=${language}&tag=${encodeTag}`;
  return requestAPIAndFireBaseEvent({
    url: requestUrl,
    method: "get",
    params: JSON.stringify({ token, language, page, display, tag, type }),
    eventName: "getHashTags",
    token,
  });
};

interface IProjectDetailProps extends IApiHeader {
  projectId: string;
}
export const getProjectDetail = async ({
  token,
  language,
  projectId,
}: IProjectDetailProps) => {
  if (!token) return { data: null, error: true };
  return requestAPIAndFireBaseEvent({
    url: `/v1/projects/${projectId}?language=${language}`,

    method: "get",
    params: JSON.stringify({ token, language, projectId }),
    eventName: "getProjectDetail",
    token,
  });
};

interface IDownloadCountProps extends Pick<IApiHeader, "token"> {
  id: string;
}
export const updateDownloadCount = async ({
  id,
  token,
}: IDownloadCountProps) => {
  if (!token) return { data: null, error: true };
  return requestAPIAndFireBaseEvent({
    url: `/v1/downloads/${id}`,

    method: "post",
    params: JSON.stringify({ token, id }),
    eventName: "updateDownloadCount",
    token,
  });
};

interface ILikeCountProps extends IDownloadCountProps {}
export const addLikeCount = async ({ id, token }: ILikeCountProps) => {
  if (!token) return { data: null, error: true };
  return requestAPIAndFireBaseEvent({
    url: `/v1/likes/${id}`,

    method: "post",
    params: JSON.stringify({ token, id }),
    eventName: "addLikeCount",
    token,
  });
};

export const deleteLikeCount = async ({ id, token }: ILikeCountProps) => {
  if (!token) return { data: null, error: true };
  return requestAPIAndFireBaseEvent({
    url: `/v1/likes/${id}`,

    method: "delete",
    params: JSON.stringify({ token, id }),
    eventName: "deleteLikeCount",
    token,
  });
};
